import {
  isValidEmail,
  isValidPhone,
  validateName,
} from './validators'

export function stepHasErrors(stepId, formData) {
  const data = formData || {}
  switch (stepId) {
    case 'details': {
      const name = (data.details?.practiceName || '').trim()
      return !name || name.length < 2
    }
    case 'billing': {
      const b = data.billing || {}
      if (typeof b.payForNonSubscriptionMedications !== 'boolean') return true
      if (typeof b.payForShippingCharges !== 'boolean') return true
      if (typeof b.practiceMarkup !== 'number' || b.practiceMarkup < 0 || b.practiceMarkup > 100) return true
      if (!['Any', 'Patient', 'Practice'].includes(b.labOrderPayer)) return true
      return false
    }
    case 'managedBilling': {
      const mb = data.managedBilling
      if (!mb) return true // never visited
      if (mb.mbEnabled === false) return false // explicitly skipped — complete
      if (mb.mbEnabled === true) {
        // Opted in — require what Step3's onValid requires: ACH connected +
        // female/male packages with names and numeric monthly/annual prices.
        const hasBank = !!mb?.achAccount?.accountId
        const pkgOk = (p) =>
          p &&
          typeof p.monthlyPrice === 'number' && p.monthlyPrice >= 1 &&
          typeof p.annualPrice === 'number' && p.annualPrice >= 1 &&
          String(p.internalName || '').trim().length >= 2 &&
          String(p.displayName || '').trim().length >= 2
        return !(hasBank && pkgOk(mb?.packages?.female) && pkgOk(mb?.packages?.male))
      }
      return true // unknown shape
    }
    case 'owners': {
      const rows = data.owners?.owners || []
      if (rows.length < 1) return true
      return !rows.every(
        (r) =>
          validateName(r?.fullName).isValid &&
          isValidPhone(r?.phoneNo) &&
          isValidEmail(r?.email),
      )
    }
    case 'locations': {
      const rows = data.locations?.locations || []
      if (rows.length < 1) return true
      const primaryCount = rows.filter((r) => r?.isPrimary === true).length
      if (primaryCount !== 1) return true
      return !rows.every(
        (r) =>
          (r?.addressLine1 || '').trim().length >= 3 &&
          (r?.city || '').trim().length >= 2 &&
          !!(r?.state || '').trim() &&
          /^\d{5}$/.test(r?.zip || ''),
      )
    }
    case 'networkplus': {
      const v = data.networkplus?.isMemberPlusMember
      return v !== true && v !== false
    }
    case 'providers': {
      const rows = data.providers?.providers || []
      const isNetworkPlus = data.networkplus?.isMemberPlusMember === true
      if (rows.length === 0) return !isNetworkPlus
      return !rows.every(
        (r) =>
          validateName(r?.fullName).isValid &&
          isValidEmail(r?.email) &&
          Array.isArray(r?.locationIds) &&
          r.locationIds.length >= 1,
      )
    }
    case 'kits': {
      const rows = data.kits?.kits || []
      if (rows.length === 0) return false
      return !rows.every((r) => {
        const hasBaseFields =
          typeof r?.quantity === 'number' &&
          r.quantity >= 1 &&
          r.quantity <= 99 &&
          (r?.recipientName || '').trim().length >= 2 &&
          isValidEmail(r?.recipientEmail)
        if (!hasBaseFields) return false
        const hasLocation =
          Array.isArray(r?.locationIds) && r.locationIds.length >= 1
        const hasCustomShipTo =
          r?.customShipTo &&
          ((r.customShipTo.addressLine1 || '').trim().length > 0 ||
            (r.customShipTo.city || '').trim().length > 0)
        return hasLocation || hasCustomShipTo
      })
    }
    case 'teams': {
      const rows = data.teams?.teams || []
      if (rows.length === 0) return false
      return !rows.every(
        (r) =>
          validateName(r?.fullName).isValid &&
          isValidEmail(r?.email) &&
          isValidPhone(r?.phoneNo) &&
          Array.isArray(r?.permissions) &&
          r.permissions.length >= 1 &&
          Array.isArray(r?.locationIds) &&
          r.locationIds.length >= 1,
      )
    }
    case 'agreements': {
      const ack = data.agreements?.agreementsAcknowledged === true
      const sig = data.agreements?.signature
      return !ack || typeof sig !== 'string' || !sig.startsWith('data:image/png;base64,')
    }
    case 'review':
    default:
      return false
  }
}

function truncate(str, max) {
  const s = (str ?? '').trim()
  if (!s) return ''
  if (s.length <= max) return s
  return s.slice(0, max - 1) + '…'
}

export function buildStepSummary(stepId, formData) {
  const data = formData || {}

  switch (stepId) {
    case 'details': {
      const name = data.details?.practiceName
      const t = truncate(name, 28)
      return t || null
    }
    case 'billing': {
      const b = data.billing || {}
      const parts = []
      if (typeof b.payForNonSubscriptionMedications === 'boolean') {
        parts.push(`Meds: ${b.payForNonSubscriptionMedications ? 'Yes' : 'No'}`)
      }
      if (typeof b.payForShippingCharges === 'boolean') {
        parts.push(`Shipping: ${b.payForShippingCharges ? 'Yes' : 'No'}`)
      }
      if (typeof b.practiceMarkup === 'number') {
        parts.push(`Markup ${Math.round(b.practiceMarkup)}%`)
      }
      if (b.labOrderPayer) parts.push(`Labs: ${b.labOrderPayer}`)
      return parts.length ? parts.join(' · ') : null
    }
    case 'managedBilling': {
      const mb = data.managedBilling
      if (mb?.acknowledged === true) return 'Confirmed'
      if (mb?.mbEnabled === false) return 'Declined'
      return null
    }
    case 'owners': {
      const rows = data.owners?.owners || []
      const named = rows.filter((r) => r?.fullName && r.fullName.trim())
      if (named.length === 0) return null
      if (named.length === 1) return truncate(named[0].fullName, 22)
      return `${named.length} owners`
    }
    case 'locations': {
      const rows = data.locations?.locations || []
      const filled = rows.filter(
        (r) => r?.addressLine1 && r.addressLine1.trim(),
      )
      const n = filled.length
      if (n === 0) return null
      const base = n === 1 ? '1 location' : `${n} locations`
      const primary = filled.find(
        (r) => r?.isPrimary === true && r?.city && r.city.trim(),
      )
      if (primary) return `${base} · ${primary.city.trim()} primary`
      return base
    }
    case 'networkplus': {
      const v = data.networkplus?.isMemberPlusMember
      if (v === true) return 'Enabled'
      if (v === false) return 'Declined'
      return null
    }
    case 'providers': {
      const rows = data.providers?.providers || []
      const named = rows.filter((r) => r?.fullName && r.fullName.trim())
      if (named.length === 0) return null
      if (named.length === 1) return truncate(named[0].fullName, 22)
      return `${named.length} providers`
    }
    case 'kits': {
      const count = data.kits?.kits?.length ?? 0
      if (count === 0) return null
      return count === 1 ? '1 kit' : `${count} kits`
    }
    case 'teams': {
      const rows = data.teams?.teams || []
      const count = rows.length
      if (count === 0) return null
      if (count === 1) {
        const n = truncate(rows[0]?.fullName, 22)
        return n || '1 member'
      }
      return `${count} members`
    }
    case 'review':
    case 'agreements':
    default:
      return null
  }
}
